import * as styles from "./AmendmentList.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useRef } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { List, ListEmpty } from "@frontend/components/atoms/List";
import { TooltipProvider } from "@frontend/components/atoms/Tooltip";
import { useArrayField } from "@frontend/hooks/form";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  addAmendmentAtom,
  contractForm,
  editedAmendmentAtom,
} from "../stores/editor";
import { AmendmentDialog } from "./AmendmentDialog";
import { AmendmentRow } from "./AmendmentRow";

export function AmendmentList(): JSX.Element {
  const { t } = useLingui();

  const price = useAtomValue(contractForm.fields.price.atom);

  const amendments = useArrayField(contractForm.fields.amendments);

  const values = useAtomValue(contractForm.fields.amendments.valueAtom);

  const edited = useAtomValue(editedAmendmentAtom);
  const setEdited = useSetAtom(editedAmendmentAtom);
  const addAmendment = useSetAtom(addAmendmentAtom);

  // The row the popup grows from.
  const originRef = useRef<DOMRect | null>(null);

  function previousPrice(index: number): string | null {
    return 0 === index ? (price ?? null) : (values[index - 1]?.price ?? null);
  }

  const editedItem =
    null === edited ? null : (amendments.items[edited.index] ?? null);

  const editedAtom =
    null === edited ? null : amendments.itemAtoms[edited.index];

  return (
    <TooltipProvider>
      <section className={styles.list}>
        <List className={styles.rows} fade separators label={t`Amendments`}>
          {amendments.isEmpty ? (
            <ListEmpty className={styles.empty}>
              <Trans>This contract has no amendment yet.</Trans>

              <Button
                variant="link"
                size="sm"
                leftSection={<Icon name="Plus" size="sm" />}
                onClick={() => addAmendment()}
              >
                {t`Add an amendment`}
              </Button>
            </ListEmpty>
          ) : (
            amendments.items.map((amendment, index) => (
              <AmendmentRow
                key={amendment.path}
                index={index}
                itemAtom={amendments.itemAtoms[index]!}
                previousPrice={previousPrice(index)}
                onOpen={(origin) => {
                  originRef.current = origin.getBoundingClientRect();
                  setEdited({ index, created: false });
                }}
                onRemove={() => amendments.remove(amendments.itemAtoms[index]!)}
              />
            ))
          )}
        </List>

        {editedItem && editedAtom && null !== edited ? (
          <AmendmentDialog
            key={edited.index}
            index={edited.index}
            fields={editedItem.fields}
            itemAtom={editedAtom}
            previousPrice={previousPrice(edited.index)}
            created={edited.created}
            onRemove={() => {
              amendments.remove(editedAtom);
              setEdited(null);
            }}
            originRef={originRef}
          />
        ) : null}
      </section>
    </TooltipProvider>
  );
}
